import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, UserPlus, Briefcase, Users, FolderTree, Calendar, FolderArchive, Calculator, UserCog,
  ScrollText, DatabaseBackup, ShieldCheck, LogOut, Menu, X, ClipboardCheck, FileUp, KeyRound,
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { AupGate } from "@/components/AupGate";
import { IsoBadge } from "@/components/IsoBadge";
import { NotificationBell } from "@/components/NotificationBell";

export const LOGO = "/logo.png";
const IDLE_MS = 30 * 60 * 1000;

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", show: () => true },
  { to: "/prospects", icon: UserPlus, label: "Nuovi Clienti", show: (a) => a.can("prospects") },
  { to: "/trusts", icon: Briefcase, label: "Progetti Trust", show: () => true },
  { to: "/subjects", icon: Users, label: "Anagrafiche", show: (a) => a.can("subjects") },
  { to: "/chart-of-accounts", icon: FolderTree, label: "Piano dei Conti", show: (a) => a.can("chart_of_accounts") },
  { to: "/deadlines", icon: Calendar, label: "Scadenzario", show: (a) => a.can("deadlines") },
  { to: "/documents", icon: FolderArchive, label: "Archivio Documenti", show: (a) => a.can("documents") },
  { to: "/fiscal", icon: Calculator, label: "Gestione Fiscale", show: (a) => a.can("fiscal") },
  { section: "Sicurezza ISO 27001", show: (a) => a.hasRole("admin", "gestore_utenti") },
  { to: "/users", icon: UserCog, label: "Gestione Utenti", show: (a) => a.hasRole("admin", "gestore_utenti") },
  { to: "/access-review", icon: ClipboardCheck, label: "Riesame Accessi", show: (a) => a.hasRole("admin", "gestore_utenti") },
  { to: "/logs", icon: ScrollText, label: "Registro Audit", show: (a) => a.hasRole("admin", "gestore_utenti") },
  { to: "/backup", icon: DatabaseBackup, label: "Backup & Ripristino", show: (a) => a.hasRole("admin") },
  { to: "/compliance", icon: ShieldCheck, label: "Conformità ISO", show: (a) => a.hasRole("admin") },
  { to: "/import", icon: FileUp, label: "Import dati Manus", show: (a) => a.hasRole("admin") },
  { to: "/integrations", icon: KeyRound, label: "Collegamenti tecnici", show: (a) => !!a.user?.integrations_owner },
];

function useIdleLogout(logout) {
  useEffect(() => {
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(logout, IDLE_MS); };
    const ev = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    ev.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(t); ev.forEach((e) => window.removeEventListener(e, reset)); };
  }, [logout]);
}

export default function Layout() {
  const auth = useAuth();
  const { user, logout } = auth;
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useIdleLogout(logout);

  const sidebar = (
    <aside className="flex flex-col h-full w-64 bg-[#0B132B] text-slate-400" data-testid="sidebar">
      <div className="px-5 py-5 flex items-center gap-3 border-b border-white/5">
        <img src={LOGO} alt="Trust Master" className="h-9 w-9 rounded-md bg-white object-contain p-0.5" />
        <div>
          <p className="font-heading text-white font-semibold leading-tight">Trust Master</p>
          <p className="text-[11px] uppercase tracking-[0.16em] text-sky-400/80">CRM · ISO 27001</p>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {NAV.filter((n) => n.show(auth)).map((n) =>
          n.section ? (
            <p key={n.section} className="px-3 pt-5 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{n.section}</p>
          ) : (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)} data-testid={`nav-${n.to.replace(/\//g, "") || "dashboard"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-white/[0.07] text-white" : "hover:bg-white/[0.04] hover:text-slate-200"}`}>
              {({ isActive }) => (<><n.icon className={`h-4 w-4 ${isActive ? "text-sky-400" : ""}`} />{n.label}</>)}
            </NavLink>
          )
        )}
      </nav>
      <div className="p-4 border-t border-white/5">
        <div className="mb-4"><IsoBadge testId="sidebar-iso-badge" /></div>
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-full bg-sky-500/15 text-sky-300 flex items-center justify-center font-semibold text-sm">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-sm text-white truncate" data-testid="current-user-name">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate" data-testid="current-user-role">{ROLE_LABELS[user?.role]}</p>
          </div>
        </div>
        <button data-testid="logout-button" onClick={async () => { await logout(); nav("/login"); }}
          className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
          <LogOut className="h-4 w-4" /> Esci
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      <div className="hidden lg:block fixed inset-y-0 left-0 z-30">{sidebar}</div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="h-full">{sidebar}</div>
          <div className="flex-1 bg-slate-900/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between px-4 h-14 bg-white/85 backdrop-blur-md border-b border-slate-200">
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-button" aria-label="Menu">{open ? <X /> : <Menu />}</button>
          <span className="font-heading font-semibold text-slate-900">Trust Master CRM</span>
          <span className="w-6" />
        </header>
        <div className="fixed top-2.5 right-4 lg:top-5 lg:right-8 z-30"><NotificationBell /></div>
        <main className="p-4 sm:p-6 lg:p-10 lg:pt-16 max-w-[1500px]"><Outlet /></main>
      </div>
      <AupGate />
    </div>
  );
}
